import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { setLoading } from "../../reducer/auth/auth";
import { getOnboardingHost } from "../../services/medium";
import {
  buildOnboardingLoginPath,
  classifyHostInfoResponse,
  rememberOnboardingHost,
  resolveOnboardingEntry,
  takeRememberedOnboardingHost,
} from "../../utils/onboardingBooking";

// How long to wait for the profile call to resolve a role before assuming the session
// is dead and sending the visitor to login.
const WAIT_FOR_ROLE_MS = 5000;

/**
 * Gate for the shared Onboarding Session booking link (/onboarding-booking/:mediumId).
 *
 * Registered in every medium route tree plus the logged out tree, because the mediums
 * this link is shared with can be approved, pending approval, mid onboarding or mid
 * integration - and only the approved tree knows /booking.
 */
export default function OnboardingBooking() {
  const { t } = useTranslation("publicMisc");
  const { mediumId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);

  const [message, setMessage] = useState("");
  const [existingAppointment, setExistingAppointment] = useState(null);

  useEffect(() => {
    const hasToken = !!localStorage.getItem("access_token");

    // Remember the host up front, not just on the way to login. A stale token, a manual
    // trip to /login or a page reload all lose the in-memory context otherwise, and
    // App.js replays this intent as soon as the role resolves to medium.
    rememberOnboardingHost(mediumId);

    const entry = resolveOnboardingEntry({
      hasToken,
      role: auth.role,
      hostId: mediumId,
    });

    if (entry.action === "wait") {
      // A token that no longer works never resolves a role, which would leave this page
      // spinning forever. Drop the dead token and hard load the login page - an in-app
      // navigate would land on the empty shell, because App only renders the login tree
      // when no token is present. The remembered host survives the trip.
      const timer = setTimeout(() => {
        localStorage.removeItem("access_token");
        window.location.href = buildOnboardingLoginPath(mediumId);
      }, WAIT_FOR_ROLE_MS);
      return () => clearTimeout(timer);
    }

    if (entry.action === "login") {
      navigate(entry.target, { replace: true });
      return;
    }

    if (entry.action === "blocked") {
      takeRememberedOnboardingHost();
      setMessage(
        entry.reason === "invalid-link"
          ? t("onboardingBooking.invalidLink")
          : t("onboardingBooking.mediumsOnly")
      );
      return;
    }

    dispatch(setLoading(true));
    getOnboardingHost(mediumId)
      .then((data) => {
        dispatch(setLoading(false));
        // Past this point the intent has been acted on, so it must not replay later.
        takeRememberedOnboardingHost();

        const outcome = classifyHostInfoResponse(data);

        if (outcome.kind === "ok") {
          navigate(entry.target, { replace: true });
          return;
        }

        if (outcome.kind === "already-booked") {
          setExistingAppointment(outcome.appointment);
          return;
        }

        if (outcome.kind === "rejected") {
          // The server's own wording: link inactive, mediums only, or self booking.
          setMessage(outcome.message || t("onboardingBooking.linkInactive"));
          return;
        }

        setMessage(
          t("onboardingBooking.couldNotOpen")
        );
      })
      .catch(() => {
        dispatch(setLoading(false));
        takeRememberedOnboardingHost();
        setMessage(t("onboardingBooking.somethingWrong"));
      });
  }, [auth.role, mediumId]);

  if (existingAppointment) {
    return (
      <div className="min-height-80vh d-flex align-items-center justify-content-center text-center px-3">
        <div>
          <h3 className="mb-3">{t("onboardingBooking.alreadyBooked")}</h3>
          <p className="mb-2">
            {t("onboardingBooking.bookedDetails", {
              date: existingAppointment.date,
              time: existingAppointment.time,
              name: existingAppointment.medium_name,
            })}
          </p>
          <Link className="coming-soon-btn d-inline-block mt-3" to="/appointments">
            {t("onboardingBooking.viewAppointments")}
          </Link>
        </div>
      </div>
    );
  }

  if (message) {
    return (
      <div className="min-height-80vh d-flex align-items-center justify-content-center text-center px-3">
        <div>
          <h3 className="mb-3">{message}</h3>
          <Link className="coming-soon-btn d-inline-block mt-3" to="/">
            {t("onboardingBooking.goBack")}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-height-80vh d-flex align-items-center justify-content-center">
      <div className="text-center">
        <h4>{t("onboardingBooking.opening")}</h4>
      </div>
    </div>
  );
}
